// [목적] PATCH /users/me 뮤테이션 훅 — 본인 프로필·알림설정·취향 저장
// [이유] settings-be-wire — SettingsPage·ProfileEditScreen·TasteScreen 에서
//         본인 정보 수정 시 단일 API 로 호출. react-query useMutation 으로 캐시 무효화 + Toast 통합
// [사이드 이팩트] 성공 시 queryKeys.user.me() 캐시 invalidate → useMe() 최신 데이터로 갱신.
//                 실패 시 ApiError.code 를 Toast 에 포함하여 사용자에게 원인 제공
// [수정 시 고려사항] BE partial merge(PATCH) 이므로 변경 필드만 전송할 것 — 전 필드 일괄 전송 금지.
//                   patchUserMeInputSchema 클라이언트 선검증 포함 (user.client.ts patchMe 내부에서 수행).
//                   notificationPrefs 는 Object.assign 서버 partial merge — 키 단위 오버라이드

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { patchMe } from '../api/user.client.js';
import { queryKeys } from '../api/queryKeys.js';
import { useToast } from '../components/feedback/Toast.js';
import { ApiError } from '../api/http.js';
import type { PatchUserMeInput } from '@gangwoncanvas/types';

// ─── 반환 타입 ───────────────────────────────────────────────────────────────

export interface UsePatchUserMeReturn {
  /** 변경된 필드만 포함한 partial 입력으로 PATCH 호출 */
  mutate: (input: PatchUserMeInput) => void;
  /** Promise 기반 mutate (async/await 패턴용) */
  mutateAsync: (input: PatchUserMeInput) => Promise<void>;
  /** 저장 진행 중 여부 */
  isPending: boolean;
}

// ─── 훅 ─────────────────────────────────────────────────────────────────────

/**
 * [목적] PATCH /users/me 뮤테이션 — 성공 시 캐시 무효화 + Toast, 실패 시 에러 Toast
 * [이유] settings-be-wire — ProfileEditScreen / TasteScreen / SettingsPage 알림 toggle 에서 공통 사용
 * [사이드 이팩트] invalidateQueries({ queryKey: ['user', 'me'] }) → useMe() refetch 트리거
 * [수정 시 고려사항] input 은 patchUserMeInputSchema partial — handle 포함 금지 (Q-12 정책)
 */
export function usePatchUserMe(): UsePatchUserMeReturn {
  const queryClient = useQueryClient();
  const toast = useToast();

  const mutation = useMutation({
    // [목적] PATCH /users/me 실제 호출 — user.client.ts patchMe 위임 (zod 선검증 포함)
    // [이유] API 레이어를 훅에서 직접 호출하지 않고 client 모듈 통해 단일화
    // [사이드 이팩트] patchMe 내부에서 patchUserMeInputSchema.parse 수행
    // [수정 시 고려사항] 스키마 변경 시 packages/types/src/user.ts 와 동기 갱신
    mutationFn: (input: PatchUserMeInput) => patchMe(input),

    // [목적] 저장 성공 후 캐시 무효화 + 성공 Toast
    // [이유] invalidate 로 useMe() 가 최신 데이터를 서버에서 다시 가져오게 함
    // [사이드 이팩트] ['user', 'me'] 캐시 stale 처리 → background refetch 트리거
    // [수정 시 고려사항] 저장 결과를 setQueryData 로 낙관적 업데이트로 바꾸고 싶다면 onMutate 에서 처리
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.user.me() });
      toast.show('저장되었습니다', 'success');
    },

    // [목적] 저장 실패 시 에러 코드 포함 Toast
    // [이유] 사용자에게 실패 원인을 간략히 안내 (code 로 디버깅 단서 제공)
    // [사이드 이팩트] 없음 — 캐시는 그대로 유지
    // [수정 시 고려사항] ApiError 외의 오류(네트워크 단절 등)도 fallback 메시지로 처리
    onError: (err: unknown) => {
      const code = err instanceof ApiError ? (err.code ?? err.status) : 'UNKNOWN';
      toast.show(`저장에 실패했습니다 (${code})`, 'error');
    },
  });

  return {
    mutate: (input: PatchUserMeInput) => mutation.mutate(input),
    mutateAsync: async (input: PatchUserMeInput) => {
      await mutation.mutateAsync(input);
    },
    isPending: mutation.isPending,
  };
}
